/**
 * #289 — the one timing standard every hover card follows (Recent events rows, map pins, map
 * group circles). Numbers and their sources are in docs/option-c-changes/289-hover-card-standard.md.
 *
 *  - First open 100 ms after the pointer rests: under the ~100–150 ms point where visual feedback
 *    starts to feel late, yet long enough that a pointer sweeping across rows or pins opens nothing.
 *  - Warm: while a card is open, or within 300 ms of one closing, the next card opens at once and
 *    without motion, so moving along a list swaps cards instead of flashing them.
 *  - Leaving starts a 150 ms grace, so the pointer can cross onto the card (WCAG 1.4.13 hoverable);
 *    coming back in time keeps it open.
 *  - Keyboard focus opens at once. Esc closes (WCAG 1.4.13 dismissible). One card on screen at a
 *    time, across every surface.
 *
 * Small Radix tooltips keep their own timer, set to the same numbers in main.tsx.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export const HOVER_OPEN_DELAY_MS = 100
export const HOVER_WARM_MS = 300
export const HOVER_CLOSE_GRACE_MS = 150

type Owner = { close: () => void }
// The card on screen (one at a time) and when the last one closed.
let current: Owner | null = null
let lastClosedAt = Number.NEGATIVE_INFINITY

const warm = () => current !== null || Date.now() - lastClosedAt < HOVER_WARM_MS

/** Tests: forget the card on screen and the warm window between tests. */
export function resetHoverIntent() {
  current = null
  lastClosedAt = Number.NEGATIVE_INFINITY
}

export interface HoverIntent {
  open: boolean
  /** Opened while warm: show it without the enter motion. */
  instant: boolean
  /** Pointer entered the item or its card. */
  enter: () => void
  /** Pointer left the item or its card. */
  leave: () => void
  /** Keyboard focus: open at once. */
  openNow: () => void
  /** Close at once (click, drag, Esc, the item went away). */
  close: () => void
}

export function useHoverIntent(): HoverIntent {
  const [state, setState] = useState({ open: false, instant: false })
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const self = useRef<Owner>({ close: () => {} })

  const clearTimers = useCallback(() => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null }
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
  }, [])

  const close = useCallback(() => {
    clearTimers()
    if (current === self.current) {
      current = null
      lastClosedAt = Date.now()
    }
    setState((s) => (s.open ? { open: false, instant: false } : s))
  }, [clearTimers])
  self.current.close = close

  const show = useCallback((instant: boolean) => {
    clearTimers()
    if (current && current !== self.current) current.close()
    current = self.current
    setState((s) => (s.open ? s : { open: true, instant }))
  }, [clearTimers])

  const enter = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
    if (current === self.current || openTimer.current) return
    if (warm()) { show(true); return }
    openTimer.current = setTimeout(() => { openTimer.current = null; show(false) }, HOVER_OPEN_DELAY_MS)
  }, [show])

  const leave = useCallback(() => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null }
    if (current !== self.current || closeTimer.current) return
    closeTimer.current = setTimeout(() => { closeTimer.current = null; close() }, HOVER_CLOSE_GRACE_MS)
  }, [close])

  const openNow = useCallback(() => show(warm()), [show])

  // Esc closes the card without the pointer or the focus moving.
  useEffect(() => {
    if (!state.open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [state.open, close])

  // Unmounted while open (the row or pin went away): free the slot and stop the timers.
  useEffect(() => () => {
    clearTimers()
    if (current === self.current) { current = null; lastClosedAt = Date.now() }
  }, [clearTimers])

  return { open: state.open, instant: state.instant, enter, leave, openNow, close }
}
